import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  Github, Linkedin, Twitter, Link as LinkIcon, Mail, Phone, 
  MapPin, Code, Briefcase, ExternalLink
} from 'lucide-react';
import LiveCodingStats from '../portfolio/LiveCodingStats';

export default function GlassmorphismTheme({ profileData, socials, activeColor }) {
  const { core, education, experience, projects, skills } = profileData;

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-100px' },
    transition: { duration: 0.6, cubicBezier: [0.16, 1, 0.3, 1] }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 font-sans selection:bg-white/30 relative overflow-hidden pb-20">
      <Helmet><title>{core.name} | Glassmorphism</title></Helmet>
      
      {/* Dynamic Animated Gradient Backgrounds */}
      <div className="fixed top-0 left-0 w-full h-full overflow-hidden -z-10">
        <motion.div 
          animate={{ x: [0, 100, 0], y: [0, -100, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          className="absolute -top-[20%] -left-[10%] w-[50vw] h-[50vw] rounded-full bg-blue-600/30 blur-[120px]" 
        />
        <motion.div 
          animate={{ x: [0, -100, 0], y: [0, 100, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
          className="absolute top-[40%] right-[10%] w-[40vw] h-[40vw] rounded-full bg-purple-600/20 blur-[120px]" 
        />
        <motion.div 
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -bottom-[20%] left-[20%] w-[60vw] h-[60vw] rounded-full bg-emerald-600/20 blur-[120px]" 
        />
      </div>

      <div className="max-w-5xl mx-auto px-6 pt-24 space-y-24">
        
        {/* Glass Hero Section */}
        <motion.section 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7 }}
          className="relative rounded-3xl overflow-hidden backdrop-blur-2xl bg-white/5 border border-white/10 p-10 sm:p-16 text-center shadow-2xl"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />
          <div className="relative z-10 space-y-6">
            <h1 className="text-5xl sm:text-7xl font-extrabold tracking-tight bg-gradient-to-br from-white to-white/40 bg-clip-text text-transparent">
              {core.name}
            </h1>
            <p className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto font-light leading-relaxed">
              {core.summary || 'Crafting digital experiences with passion and precision.'}
            </p>
            
            {/* Social Links inside Glass Pill */}
            <div className="inline-flex items-center gap-4 px-6 py-3 rounded-full backdrop-blur-md bg-white/10 border border-white/20 mt-8">
              {socials.github && <a href={socials.github} target="_blank" rel="noreferrer" className="text-white/60 hover:text-white transition-colors"><Github className="w-5 h-5" /></a>}
              {socials.linkedin && <a href={socials.linkedin} target="_blank" rel="noreferrer" className="text-white/60 hover:text-white transition-colors"><Linkedin className="w-5 h-5" /></a>}
              {socials.twitter && <a href={socials.twitter} target="_blank" rel="noreferrer" className="text-white/60 hover:text-white transition-colors"><Twitter className="w-5 h-5" /></a>}
              {socials.custom && <a href={socials.custom} target="_blank" rel="noreferrer" className="text-white/60 hover:text-white transition-colors"><LinkIcon className="w-5 h-5" /></a>}
              {(!socials.github && !socials.linkedin && !socials.twitter && !socials.custom) && <span className="text-white/40 text-xs">No links added</span>}
            </div>
          </div>
        </motion.section>

        {/* Two-column layout for details */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          
          {/* Left Column */}
          <div className="md:col-span-8 space-y-8">
            {experience.length > 0 && (
              <motion.section {...fadeInUp} className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-8 shadow-xl">
                <h2 className="text-2xl font-bold mb-8 flex items-center gap-3 text-white/90">
                  <Briefcase className={`w-6 h-6 ${activeColor.primary}`} /> 
                  Experience
                </h2>
                <div className="space-y-8">
                  {experience.map((exp, idx) => (
                    <div key={idx} className="relative pl-6 border-l border-white/20">
                      <div className={`absolute -left-1.5 top-1.5 w-3 h-3 rounded-full ${activeColor.bg} shadow-[0_0_10px_currentColor]`} />
                      <span className="text-xs font-semibold text-white/40 tracking-wider uppercase block mb-1">
                        {exp.start_date} - {exp.end_date || 'Present'}
                      </span>
                      <h3 className="text-lg font-bold text-white/90">{exp.job_title}</h3>
                      <div className="text-sm text-white/60 mb-3">{exp.company}</div>
                      <p className="text-sm text-white/50 leading-relaxed">{exp.description}</p>
                    </div>
                  ))}
                </div>
              </motion.section>
            )}

            {projects.length > 0 && (
              <motion.section {...fadeInUp} className="space-y-6">
                <h2 className="text-2xl font-bold px-2 flex items-center gap-3 text-white/90">
                  <Code className={`w-6 h-6 ${activeColor.primary}`} /> 
                  Featured Projects
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {projects.map((proj, idx) => (
                    <div key={idx} className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-6 shadow-lg hover:bg-white/10 transition-colors flex flex-col justify-between group">
                      <div>
                        <h3 className="text-lg font-bold text-white/90 mb-2">{proj.title}</h3>
                        <p className="text-sm text-white/50 leading-relaxed mb-4">{proj.description}</p>
                      </div>
                      {proj.link && (
                        <a href={proj.link} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-1.5 text-sm font-semibold ${activeColor.primary} opacity-80 group-hover:opacity-100 transition-opacity`}>
                          View Project <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </motion.section>
            )}
          </div>

          {/* Right Column */}
          <div className="md:col-span-4 space-y-8">
            <motion.section {...fadeInUp} className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-8 shadow-xl">
              <h2 className="text-xl font-bold mb-6 text-white/90">Contact Details</h2>
              <div className="space-y-4 text-sm text-white/60">
                {core.email && (
                  <div className="flex items-center gap-3 bg-white/5 p-3 rounded-xl border border-white/5">
                    <Mail className="w-4 h-4" /> <span className="truncate">{core.email}</span>
                  </div>
                )}
                {core.phone && (
                  <div className="flex items-center gap-3 bg-white/5 p-3 rounded-xl border border-white/5">
                    <Phone className="w-4 h-4" /> <span>{core.phone}</span>
                  </div>
                )}
                {core.city && (
                  <div className="flex items-center gap-3 bg-white/5 p-3 rounded-xl border border-white/5">
                    <MapPin className="w-4 h-4" /> <span>{core.city}, {core.country}</span>
                  </div>
                )}
              </div>
            </motion.section>

            {skills.length > 0 && (
              <motion.section {...fadeInUp} className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-8 shadow-xl">
                <h2 className="text-xl font-bold mb-6 text-white/90">Skills</h2>
                <div className="flex flex-wrap gap-2">
                  {skills.map((sk, idx) => (
                    <span key={idx} className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/10 text-xs text-white/80">
                      {sk.name || sk}
                    </span>
                  ))}
                </div>
              </motion.section>
            )}
            
            {education.length > 0 && (
              <motion.section {...fadeInUp} className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-8 shadow-xl">
                <h2 className="text-xl font-bold mb-6 text-white/90">Education</h2>
                <div className="space-y-5">
                  {education.map((edu, idx) => (
                    <div key={idx} className="space-y-1">
                      <div className="text-sm font-bold text-white/90">{edu.degree}</div>
                      <div className="text-xs text-white/60">{edu.school}</div>
                      <div className="text-[10px] text-white/40 uppercase tracking-widest">{edu.start_date} - {edu.end_date || 'Present'}</div>
                    </div>
                  ))}
                </div>
              </motion.section>
            )}

            {/* Live Coding Stats */}
            {(socials.github || socials.leetcode) && (
              <motion.section {...fadeInUp} className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl shadow-xl">
                 <LiveCodingStats githubUrl={socials.github} leetcodeUrl={socials.leetcode} activeColor={activeColor} themeType="dark" />
              </motion.section>
            )}

          </div>
        </div>

      </div>
    </div>
  );
}
